import { FONT_FAMILY } from '../constants';
import { Text } from '../Text';
import { type TextLevel, getFontSizeStyle } from '../Text/TextLevel';
import { useTheme } from '../ThemeProvider';

import { useMemo } from 'react';
import {
    type Text as RNText,
    StyleSheet,
    type TextProps,
    type TextStyle,
    useWindowDimensions,
} from 'react-native';

const MARGIN_BOTTOM = {
    1: 1,
    2: 1,
    3: 2,
    4: 2,
    5: 2,
    6: 4,
    7: 4,
    8: 4,
};

export interface HeadingProps extends TextProps {
    /**
     * Allows getting a ref to the component instance.
     * Once the component unmounts, React will set `ref.current` to `null`
     * (or call the ref with `null` if you passed a callback ref).
     *
     * @see {@link https://react.dev/learn/referencing-values-with-refs#refs-and-the-dom React Docs}
     */
    ref?: React.Ref<RNText>;

    /**
     * Visual text level of the Heading. Use values `1` (smallest) to `8` (largest).
     * Controls font size and margin. Also sets the semantic `aria-level` unless
     * overridden by `ariaLevel`.
     *
     * Defaults to 5.
     */
    level?: TextLevel;

    /**
     * Overrides the semantic heading level (aria-level) independently of the
     * visual text size. When omitted, `aria-level` defaults to `level`.
     */
    ariaLevel?: number;

    /**
     * additional styles on Tag component, please pass the general CSS
     */
    sx?: TextStyle;

    /**
     * label to show to screen readers
     * This defaults to true on all platforms *except* Android.
     * On Android setting selectable to true will make the text focusable and add it to the keyboard focus order.
     * See A11Y-7173 for additional details (https://indeed.atlassian.net/browse/A11Y-7173).
     */
    selectable?: boolean;

    /**
     * The number of lines to show before applying an ellipsis
     */
    numberOfLines?: number;

    /**
     * Optional press handler
     */
    onPress?: () => void;
}

export function Heading({
    ref,
    level = 5,
    ariaLevel,
    children,
    sx = {},
    selectable,
    numberOfLines,
    ...props
}: HeadingProps) {
    const theme = useTheme();
    const { fontScale } = useWindowDimensions();
    const style: TextStyle = useMemo(
        () => ({
            alignSelf: 'flex-start', // required for right-to-left support
            textAlign: 'left', // required for right-to-left support
            marginBottom: MARGIN_BOTTOM[level],
            color: theme.colors.foregroundStrong,
            fontWeight: 'bold',
            fontFamily: FONT_FAMILY,
            ...getFontSizeStyle(level),
            letterSpacing: fontScale * 0.0001, //Force a layout recalculation when font scale changes
        }),
        [fontScale, level, theme],
    );

    const mergedStyle = StyleSheet.flatten([style, sx]);

    return (
        <Text
            ref={ref}
            level={level}
            sx={mergedStyle}
            selectable={selectable}
            accessibilityRole="header"
            ariaLevel={ariaLevel ?? level}
            numberOfLines={numberOfLines}
            {...props}
        >
            {children}
        </Text>
    );
}
